import React, { useEffect, useRef, useState } from 'react';
import { HashRouter, Location, useLocation, useNavigate } from 'react-router';
import { AzureRouterProps, NavigationService } from './types';

async function getNav(): Promise<NavigationService> {
  const nav = await VSS.getService(VSS.ServiceIds.Navigation);
  return nav as unknown as NavigationService;
}

function normalizeHash(hash: string) {
  const value = (hash || '').trim().replace(/^#/, '');
  if (!value) return '/';
  return value.startsWith('/') ? value : `/${value}`;
}

async function getInitialHash(): Promise<string> {
  try {
    const nav = await getNav();
    const hash = await nav.getHash();
    return normalizeHash(hash);
  } catch {
    return '/';
  }
}

function getRoute(location: Location) {
  return `${location.pathname}${location.search}`;
}

function SyncHostWithHash() {
  const location = useLocation();
  const navigate = useNavigate();

  const navRef = useRef<NavigationService | null>(null);
  const bootstrappedRef = useRef(false);
  const syncFromHostRef = useRef(false);
  const lastKnownRouteRef = useRef('/');

  const routeHandler = (hash: string) => {
    const nextHashRoute = normalizeHash(hash);
    const currentRoute = lastKnownRouteRef.current;

    if (nextHashRoute === currentRoute) return;
    syncFromHostRef.current = true;
    lastKnownRouteRef.current = nextHashRoute;
    navigate(nextHashRoute, { replace: true });
  };

  useEffect(() => {
    let disposed = false;
    let unsubscribe: (() => void) | undefined;

    (async () => {
      const nav = await getNav();
      if (disposed) return;

      navRef.current = nav;
      const currentRoute = getRoute(location);
      const hostHash = await nav.getHash();
      const hostRoute = normalizeHash(await nav.getHash());

      if (hostHash && hostRoute !== '/' && hostRoute !== currentRoute) {
        syncFromHostRef.current = true;
        lastKnownRouteRef.current = hostRoute;
        navigate(hostRoute, { replace: true });
      } else {
        lastKnownRouteRef.current = currentRoute;
        nav.replaceHash(currentRoute);
      }

      nav.onHashChanged(routeHandler);
      if (nav.offHashChanged) {
        unsubscribe = () => nav.offHashChanged?.(routeHandler);
      }

      bootstrappedRef.current = true;
    })();

    return () => {
      disposed = true;
      unsubscribe?.();
    };
  }, [location, navigate]);

  useEffect(() => {
    if (!bootstrappedRef.current) return;
    if (!navRef.current) return;
    const nav = navRef.current;
    const currentRoute = getRoute(location);
    if (syncFromHostRef.current) {
      syncFromHostRef.current = false;
      lastKnownRouteRef.current = currentRoute;
      return;
    }

    lastKnownRouteRef.current = currentRoute;
    nav.replaceHash(currentRoute);
  }, [bootstrappedRef, location, navRef, syncFromHostRef]);

  return null;
}

function AzureRouter({ children }: AzureRouterProps) {
  const [isReady, setIsReady] = useState(false);

  // Ensure the hash is synced before rendering the router
  useEffect(() => {
    const initializeHash = async () => {
      window.location.hash = await getInitialHash();
      setIsReady(true);
    };

    initializeHash();
  }, []);

  if (!isReady) return null;

  return (
    <HashRouter>
      <SyncHostWithHash />
      {children}
    </HashRouter>
  );
}

export default AzureRouter;
